import { httpClient } from '@/core/api/httpClient';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import type { GanttDto, GanttDependencyDto, AddTaskDependencyRequest } from '../types';
import { getMockGanttDependencies } from './gantt.mock';

/**
 * Dependency edges only. `GanttDto.tasks` is intentionally ignored — rows come
 * from the rich task list (`tasksService.getQuery` with `view=gantt`), which
 * carries status/priority/parent/group that the minimal `GanttTaskDto` lacks.
 */
async function getDependencies(projectId: string): Promise<GanttDependencyDto[]> {
  if (env.VITE_USE_MOCK === 'true') {
    return getMockGanttDependencies(projectId);
  }
  const { data } = await httpClient.get<GanttDto>(`${API_V1}/projects/${projectId}/gantt`);
  return data.dependencies ?? [];
}

/**
 * Create a finish-to-start dependency. `successorTaskId` is the dependent task
 * (the endpoint's `{taskId}`); the body names its predecessor.
 */
async function addDependency(
  projectId: string,
  successorTaskId: string,
  body: AddTaskDependencyRequest,
): Promise<void> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve();
  }
  await httpClient.post<AddTaskDependencyRequest, void>(
    `${API_V1}/projects/${projectId}/tasks/${successorTaskId}/dependencies`,
    body,
  );
}

async function removeDependency(
  projectId: string,
  successorTaskId: string,
  dependencyId: string,
): Promise<void> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve();
  }
  await httpClient.delete<void>(
    `${API_V1}/projects/${projectId}/tasks/${successorTaskId}/dependencies/${dependencyId}`,
  );
}

export const ganttService = {
  getDependencies,
  addDependency,
  removeDependency,
};
